/** @jsx jsx */
import * as React from 'react';
import { useCombobox, UseComboboxProps } from 'downshift';
import { jsx, css } from '@emotion/core';
import cx from 'classnames';
import { useDeepCompareEffect } from 'react-use';
import Highlighter from 'react-highlight-words';

import AddBlock from '@clearkit/icons/glyphs/AddBlock';
import Caret from '@clearkit/icons/glyphs/Caret';
import Checkmark from '@clearkit/icons/glyphs/Checkmark';

import { CKSelectListItem } from '../CKSelectListItem';
import { usePopover } from '../../hooks';
import {
  ItemBase,
  CommonSelectProps,
  CKPickerVariant,
} from '../../common/types';
import {
  defaultItemRenderer,
  defaultEmptyState,
  defaultOptionFilterFunc,
} from '../../common/utils';

export interface CKPickerProps<T extends ItemBase>
  extends UseComboboxProps<T>,
    CommonSelectProps<T> {
  isCreateable?: boolean;
  isDisabled?: boolean;
  isSmall?: boolean;
  variant?: CKPickerVariant;
  onCreateItem?: (item: T) => void;
  itemRenderer?: (item: T) => string;
  emptyState?: (inputValue: string) => React.ReactNode;
  optionFilterFunc?: (items: T[], inputValue: string) => T[];
}

export const CKPicker = <T extends ItemBase>(
  props: CKPickerProps<T>
): React.ReactElement<CKPickerProps<T>> => {
  const [isCreating, setIsCreating] = React.useState(false);
  const [inputItems, setInputItems] = React.useState<T[]>(props.items);
  const popperReference = React.useRef<HTMLDivElement>(null);
  const popperPopover = React.useRef<HTMLDivElement>(null);
  const popper = usePopover({
    popperReference,
    popperPopover,
    popperOffset: props.popperOffset,
    popperPlacement: props.popperPlacement,
    popperModifiers: props.popperModifiers,
  });

  useDeepCompareEffect(() => {
    setInputItems(props.items);
  }, [props.items]);

  const {
    placeholder,
    leftIcon,
    isDisabled = false,
    isSmall = false,
    variant = 'default',
    itemRenderer = defaultItemRenderer,
    itemMetaRenderer = null,
    isItemDisabled = () => false,
    emptyState = defaultEmptyState,
    optionFilterFunc = defaultOptionFilterFunc,
    isCreateable,
    onCreateItem,
    popperZIndex = 10,
    ...downshiftProps
  } = props;
  const {
    isOpen,
    selectedItem,
    getToggleButtonProps,
    getLabelProps,
    getMenuProps,
    getInputProps,
    getComboboxProps,
    highlightedIndex,
    getItemProps,
    inputValue,
    openMenu,
    setHighlightedIndex,
  } = useCombobox({
    ...downshiftProps,
    items: inputItems,
    itemToString: downshiftProps.itemToString,
    onInputValueChange: ({ inputValue }) => {
      const filteredItems = optionFilterFunc(
        downshiftProps.items,
        inputValue || ''
      );

      if (isCreateable && isCreating && filteredItems.length > 0) {
        setIsCreating(false);
      }

      setInputItems(filteredItems);
    },
    onStateChange: changes => {
      if (isCreateable) {
        if (
          changes.selectedItem?.value === inputValue &&
          !downshiftProps.items.includes(changes.selectedItem)
        ) {
          // @ts-ignore
          onCreateItem(changes.selectedItem);
          setInputItems(downshiftProps.items);
          setIsCreating(false);
        }
      }
    },
  });

  const outerPickerClass = cx('ck-picker', {
    'ck-picker--open': isOpen,
    'ck-picker--error': variant === 'error',
    'ck-picker--success': variant === 'success',
    'ck-picker--disabled': isDisabled,
    'ck-picker--small': isSmall,
  });

  React.useEffect(() => {
    if (inputItems.length === 0 && isCreateable) {
      setIsCreating(true);
      // @ts-ignore
      setInputItems([{ label: `${inputValue}`, value: inputValue }]);
      setHighlightedIndex(0);
    }
  }, [
    inputItems,
    isCreateable,
    setIsCreating,
    setHighlightedIndex,
    inputValue,
  ]);

  React.useEffect(() => {
    if (isOpen && popper.current) {
      popper.current?.forceUpdate();
    }
  }, [isOpen, popper]);

  return (
    <div className={outerPickerClass} ref={popperReference}>
      <div
        className="ck-picker-toggle ck-picker-toggle--typeable"
        {...getComboboxProps()}
      >
        <label className="ck-sr-only" {...getLabelProps()}>
          {placeholder}
        </label>
        {leftIcon && (
          <span className="ck-picker-icon ck-picker-icon--left">
            {leftIcon}
          </span>
        )}
        <input
          className="ck-picker-value"
          disabled={isDisabled}
          css={css`
            width: 100%;
          `}
          {...getInputProps({
            placeholder,
            onFocus: () => {
              if (!isOpen) {
                openMenu();
              }
            },
          })}
        />
        <button
          disabled={isDisabled}
          css={css`
            display: flex;
          `}
          {...getToggleButtonProps({ type: 'button' })}
          aria-label="toggle menu"
        >
          <span className="ck-picker-icon ck-picker-icon--right">
            <Caret />
          </span>
        </button>
      </div>
      <div
        css={css`
          visibility: ${isOpen ? 'visible' : 'hidden'};
          pointer-events: ${isOpen ? 'auto' : 'none'};
          z-index: ${popperZIndex};
        `}
        className="ck-popover ck-popover--full-width"
        {...getMenuProps({ ref: popperPopover })}
      >
        <ul
          className="ck-menu ck-menu--options"
          css={css`
            outline: 0;
          `}
        >
          {isOpen && inputItems.length === 0 && emptyState(inputValue)}
          {isOpen && inputItems.length > 0 && (
            <React.Fragment>
              {inputItems.map((item, index) => (
                <CKSelectListItem
                  key={`${item}${index}`}
                  isHighlighted={highlightedIndex === index}
                  isSelected={item.value === selectedItem?.value}
                  isDisabled={isItemDisabled(item)}
                  {...getItemProps({
                    item,
                    index,
                    disabled: isItemDisabled(item),
                  })}
                  accessoryRenderer={
                    isCreating ? (
                      <AddBlock />
                    ) : (
                      selectedItem?.value === item.value && <Checkmark />
                    )
                  }
                  itemMetaRenderer={itemMetaRenderer && itemMetaRenderer(item)}
                >
                  {isCreating ? (
                    <p>
                      <span>Create</span>{' '}
                      <span className="font-medium">{item.label}</span>
                    </p>
                  ) : (
                    <Highlighter
                      autoEscape
                      searchWords={[inputValue || '']}
                      textToHighlight={itemRenderer(item)}
                      highlightClassName="ck-highlight"
                    ></Highlighter>
                  )}
                </CKSelectListItem>
              ))}
            </React.Fragment>
          )}
        </ul>
      </div>
    </div>
  );
};
